import { useState } from 'react';
import { comparisonModels, sideBySideComparison } from '../data';

export default function SideBySideSection() {
  const [selectedScenario, setSelectedScenario] = useState(0);
  const scenario = sideBySideComparison[selectedScenario];

  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-background-200 to-transparent" />

      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">
        {/* Section Header */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[40px] font-[750] leading-[1.12] text-foreground-950 tracking-[-0.015em] max-w-[700px]">
            Two pricing models, one cost breakdown.
          </h2>
        </div>

        {/* ── Models Comparison ── */}
        <div className="mb-12 md:mb-16">

          {/* ── Desktop: unified two-column bordered block ── */}
          <div className="hidden md:block">
            <div className="grid grid-cols-2 border border-background-200/70 rounded-t-2xl overflow-hidden">
              <div className="px-10 py-5 border-r border-background-200/70 bg-primary-50/30">
                <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-primary-600">{comparisonModels[0].name}</div>
                <div className="text-[13px] text-foreground-500 mt-0.5 font-medium">{comparisonModels[0].broker}</div>
              </div>
              <div className="px-10 py-5 bg-accent-50/30">
                <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-accent-600">{comparisonModels[1].name}</div>
                <div className="text-[13px] text-foreground-500 mt-0.5 font-medium">{comparisonModels[1].broker}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 border-x border-b border-background-200/70 bg-background-50">
              <div className="px-10 py-10 border-r border-background-200/70">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-3">Cost per standard lot (EURUSD)</div>
                <span className="text-[68px] font-[750] text-primary-600 leading-none tabular-nums">
                  ${comparisonModels[0].costPerLotTotal.toFixed(2)}
                </span>
              </div>
              <div className="px-10 py-10">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-3">Cost per standard lot (EURUSD)</div>
                <span className="text-[68px] font-[750] text-accent-600 leading-none tabular-nums">
                  ${comparisonModels[1].costPerLotTotal.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 border-x border-b border-background-200/70 bg-background-50">
              <div className="px-10 py-6 border-r border-background-200/70 space-y-2.5">
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Spread</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[0].spreadLabel}</span>
                </div>
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Commission</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[0].commissionLabel}</span>
                </div>
              </div>
              <div className="px-10 py-6 space-y-2.5">
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Spread</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[1].spreadLabel}</span>
                </div>
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Commission</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[1].commissionLabel}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 border-x border-b border-background-200/70 rounded-b-2xl overflow-hidden bg-background-50">
              <div className="px-10 py-7 border-r border-background-200/70">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2.5">Best for</div>
                <p className="text-[14px] text-foreground-700 leading-relaxed mb-5">{comparisonModels[0].bestFor}</p>
                <div className="space-y-2.5">
                  {comparisonModels[0].pros.map((pro, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-[3px] h-[3px] rounded-full bg-primary-500 mt-[9px] flex-shrink-0" />
                      <span className="text-[13px] text-foreground-600 leading-relaxed">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-10 py-7">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2.5">Best for</div>
                <p className="text-[14px] text-foreground-700 leading-relaxed mb-5">{comparisonModels[1].bestFor}</p>
                <div className="space-y-2.5">
                  {comparisonModels[1].pros.map((pro, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-[3px] h-[3px] rounded-full bg-accent-500 mt-[9px] flex-shrink-0" />
                      <span className="text-[13px] text-foreground-600 leading-relaxed">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Mobile: stacked independent cards ── */}
          <div className="md:hidden flex flex-col gap-0">
            {/* Card A */}
            <div className="rounded-2xl border border-background-200/70 overflow-hidden bg-background-50">
              {/* Header */}
              <div className="px-5 py-4 bg-primary-50/40 border-b border-background-200/70">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary-600">{comparisonModels[0].name}</div>
                <div className="text-[12px] text-foreground-500 mt-0.5 font-medium">{comparisonModels[0].broker}</div>
              </div>
              {/* Cost */}
              <div className="px-5 py-5 border-b border-background-200/70">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Cost per standard lot (EURUSD)</div>
                <span className="text-[52px] font-[750] text-primary-600 leading-none tabular-nums">
                  ${comparisonModels[0].costPerLotTotal.toFixed(2)}
                </span>
              </div>
              {/* Details */}
              <div className="px-5 py-4 border-b border-background-200/70 space-y-2.5">
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Spread</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[0].spreadLabel}</span>
                </div>
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Commission</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[0].commissionLabel}</span>
                </div>
              </div>
              {/* Best for */}
              <div className="px-5 py-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Best for</div>
                <p className="text-[14px] text-foreground-700 leading-relaxed mb-4">{comparisonModels[0].bestFor}</p>
                <div className="space-y-2.5">
                  {comparisonModels[0].pros.map((pro, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-[3px] h-[3px] rounded-full bg-primary-500 mt-[9px] flex-shrink-0" />
                      <span className="text-[13px] text-foreground-600 leading-relaxed">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* VS divider */}
            <div className="flex items-center justify-center py-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-[11px] font-bold text-foreground-500 tracking-widest">
                VS
              </span>
            </div>

            {/* Card B */}
            <div className="rounded-2xl border border-background-200/70 overflow-hidden bg-background-50">
              {/* Header */}
              <div className="px-5 py-4 bg-accent-50/40 border-b border-background-200/70">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-accent-600">{comparisonModels[1].name}</div>
                <div className="text-[12px] text-foreground-500 mt-0.5 font-medium">{comparisonModels[1].broker}</div>
              </div>
              {/* Cost */}
              <div className="px-5 py-5 border-b border-background-200/70">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Cost per standard lot (EURUSD)</div>
                <span className="text-[52px] font-[750] text-accent-600 leading-none tabular-nums">
                  ${comparisonModels[1].costPerLotTotal.toFixed(2)}
                </span>
              </div>
              {/* Details */}
              <div className="px-5 py-4 border-b border-background-200/70 space-y-2.5">
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Spread</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[1].spreadLabel}</span>
                </div>
                <div className="flex items-baseline justify-between text-[14px]">
                  <span className="text-foreground-500">Commission</span>
                  <span className="font-semibold text-foreground-900">{comparisonModels[1].commissionLabel}</span>
                </div>
              </div>
              {/* Best for */}
              <div className="px-5 py-5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Best for</div>
                <p className="text-[14px] text-foreground-700 leading-relaxed mb-4">{comparisonModels[1].bestFor}</p>
                <div className="space-y-2.5">
                  {comparisonModels[1].pros.map((pro, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-[3px] h-[3px] rounded-full bg-accent-500 mt-[9px] flex-shrink-0" />
                      <span className="text-[13px] text-foreground-600 leading-relaxed">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Scenario Comparison ── */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4 mb-8">
            <h3 className="text-[22px] md:text-[26px] font-[750] text-foreground-950 tracking-[-0.01em]">
              Real trade scenarios
            </h3>
            <span className="text-[14px] text-foreground-500">
              Pick a scenario to compare costs line by line
            </span>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {sideBySideComparison.map((s, i) => (
              <button
                key={i}
                onClick={() => setSelectedScenario(i)}
                className={`px-5 py-2.5 rounded-full text-[13px] font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  i === selectedScenario
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* ── Desktop scenario panel ── */}
          <div className="hidden md:block rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden">
            <div className="px-10 py-4 border-b border-background-200/70">
              <p className="text-[15px] font-semibold text-foreground-800">{scenario.scenario}</p>
            </div>
            <div className="grid grid-cols-2">
              <div className="px-10 py-8 border-r border-background-200/70">
                <div className="text-[12px] font-bold uppercase tracking-[0.10em] text-primary-600 mb-6">Spread-Only Account</div>
                <div className="space-y-3 mb-7">
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Spread</span>
                    <span className="font-semibold text-foreground-900">{scenario.spreadOnly.spreadPips} pips</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Spread cost</span>
                    <span className="font-semibold text-foreground-900">${scenario.spreadOnly.spreadCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Commission</span>
                    <span className="text-foreground-400 font-medium">None</span>
                  </div>
                </div>
                <div className="pt-5 border-t border-background-200/70">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Total cost</div>
                  <div className="text-[48px] font-[750] text-primary-600 leading-none tabular-nums">${scenario.spreadOnly.total.toFixed(2)}</div>
                </div>
              </div>
              <div className="px-10 py-8">
                <div className="text-[12px] font-bold uppercase tracking-[0.10em] text-accent-600 mb-6">ECN / Commission Account</div>
                <div className="space-y-3 mb-7">
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Raw spread</span>
                    <span className="font-semibold text-foreground-900">{scenario.commissionOnly.spreadPips} pips</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Spread cost</span>
                    <span className="font-semibold text-foreground-900">${scenario.commissionOnly.spreadCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[15px]">
                    <span className="text-foreground-500">Commission</span>
                    <span className="font-semibold text-accent-700">${scenario.commissionOnly.commission.toFixed(2)}</span>
                  </div>
                </div>
                <div className="pt-5 border-t border-background-200/70">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Total cost</div>
                  <div className="text-[48px] font-[750] text-accent-600 leading-none tabular-nums">${scenario.commissionOnly.total.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className={`px-10 py-4 border-t border-background-200/70 ${
              scenario.winner === 'commission' ? 'bg-accent-50/60' : scenario.winner === 'spread' ? 'bg-primary-50/60' : 'bg-background-100/60'
            }`}>
              <div className="flex items-center gap-5">
                <span className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] ${
                  scenario.winner === 'commission' ? 'bg-accent-500/15 text-accent-700' : scenario.winner === 'spread' ? 'bg-primary-500/15 text-primary-700' : 'bg-foreground-500/15 text-foreground-700'
                }`}>
                  {scenario.winner === 'commission' ? 'ECN Wins' : scenario.winner === 'spread' ? 'Spread Wins' : 'Even'}
                </span>
                <span className="text-[15px] font-semibold text-foreground-700">{scenario.savings}</span>
              </div>
              <p className="text-[14px] text-foreground-500 mt-1.5 leading-relaxed">{scenario.note}</p>
            </div>
          </div>

          {/* ── Mobile scenario panel: stacked cards ── */}
          <div className="md:hidden">
            <div className="mb-3 px-1">
              <p className="text-[13px] font-semibold text-foreground-700">{scenario.scenario}</p>
            </div>

            <div className="flex flex-col gap-0">
              {/* Spread-Only card */}
              <div className="rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden">
                <div className="px-5 py-3.5 bg-primary-50/40 border-b border-background-200/70">
                  <div className="text-[11px] font-bold uppercase tracking-[0.10em] text-primary-600">Spread-Only Account</div>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Spread</span>
                    <span className="font-semibold text-foreground-900">{scenario.spreadOnly.spreadPips} pips</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Spread cost</span>
                    <span className="font-semibold text-foreground-900">${scenario.spreadOnly.spreadCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Commission</span>
                    <span className="text-foreground-400 font-medium">None</span>
                  </div>
                </div>
                <div className="px-5 pt-3 pb-5 border-t border-background-200/70">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Total cost</div>
                  <div className="text-[44px] font-[750] text-primary-600 leading-none tabular-nums">${scenario.spreadOnly.total.toFixed(2)}</div>
                </div>
              </div>

              {/* VS divider */}
              <div className="flex items-center justify-center py-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-[11px] font-bold text-foreground-500 tracking-widest">
                  VS
                </span>
              </div>

              {/* ECN card */}
              <div className="rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden">
                <div className="px-5 py-3.5 bg-accent-50/40 border-b border-background-200/70">
                  <div className="text-[11px] font-bold uppercase tracking-[0.10em] text-accent-600">ECN / Commission Account</div>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Raw spread</span>
                    <span className="font-semibold text-foreground-900">{scenario.commissionOnly.spreadPips} pips</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Spread cost</span>
                    <span className="font-semibold text-foreground-900">${scenario.commissionOnly.spreadCost.toFixed(2)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-[14px]">
                    <span className="text-foreground-500">Commission</span>
                    <span className="font-semibold text-accent-700">${scenario.commissionOnly.commission.toFixed(2)}</span>
                  </div>
                </div>
                <div className="px-5 pt-3 pb-5 border-t border-background-200/70">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.10em] text-foreground-400 mb-2">Total cost</div>
                  <div className="text-[44px] font-[750] text-accent-600 leading-none tabular-nums">${scenario.commissionOnly.total.toFixed(2)}</div>
                </div>
              </div>

              {/* Verdict */}
              <div className={`mt-3 rounded-2xl px-5 py-4 border border-background-200/70 ${
                scenario.winner === 'commission' ? 'bg-accent-50/60' : scenario.winner === 'spread' ? 'bg-primary-50/60' : 'bg-background-100/60'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] ${
                    scenario.winner === 'commission' ? 'bg-accent-500/15 text-accent-700' : scenario.winner === 'spread' ? 'bg-primary-500/15 text-primary-700' : 'bg-foreground-500/15 text-foreground-700'
                  }`}>
                    {scenario.winner === 'commission' ? 'ECN Wins' : scenario.winner === 'spread' ? 'Spread Wins' : 'Even'}
                  </span>
                  <span className="text-[14px] font-semibold text-foreground-700">{scenario.savings}</span>
                </div>
                <p className="text-[13px] text-foreground-500 leading-relaxed">{scenario.note}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}